import { Inject, Injectable } from '@angular/core';
import {
  Device,
  DeviceParameterInfo,
  DeviceRef,
  makeDeviceRefObj,
  MotionMasterClient,
  MotionMasterMessage,
} from '@oblac/motion-master-client';
import { MotionMasterReqResWorkerSocket, MotionMasterPubSubWorkerSocket } from '@oblac/motion-master-client';
import { PUB_SUB_WORKER, REQ_RES_WORKER } from './injection-tokens';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class MotionMasterService {
  readonly client: MotionMasterClient;

  devices$ = new BehaviorSubject<Device[] | null>(null);

  private parameterInfoCache = new Map<DeviceRef, Observable<DeviceParameterInfo[]>>();

  private constructor(
    @Inject(REQ_RES_WORKER) readonly reqResWorker: Worker,
    @Inject(PUB_SUB_WORKER) readonly pubSubWorker: Worker,
    private router: Router
  ) {
    const reqResSocket = new MotionMasterReqResWorkerSocket(reqResWorker);
    const pubSubSocket = new MotionMasterPubSubWorkerSocket(pubSubWorker);

    this.client = new MotionMasterClient(reqResSocket, pubSubSocket);

    this.client.monitor.systemEvent$.subscribe((systemEvent) => {
      if (systemEvent.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING) {
        this.devices$.next(null);
        this.parameterInfoCache.clear();
        this.router.navigateByUrl('/');
      } else if (systemEvent.state === MotionMasterMessage.Status.SystemEvent.State.INITIALIZED) {
        this.getDevices();
      }
    });
  }

  getDevices() {
    this.client.request
      .getDevices()
      .subscribe((devices) => this.devices$.next(devices.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))));
  }

  getDeviceParameterInfo(deviceRef: DeviceRef): Observable<DeviceParameterInfo[]> {
    let cached = this.parameterInfoCache.get(deviceRef);
    if (!cached) {
      cached = this.client.request.getDeviceParameterInfo(makeDeviceRefObj(deviceRef), 5000).pipe(
        map((status) => (status.parameters as DeviceParameterInfo[]) ?? []),
        shareReplay(1)
      );
      this.parameterInfoCache.set(deviceRef, cached);
    }
    return cached;
  }
}
